import { createFileRoute, Link, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { LogOut, Moon, Settings, Shield, Sun, UserRound } from "lucide-react";
import { useState } from "react";
import { Brand } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { DropdownMenu, MenuItem, MenuSeparator } from "@/components/ui/menu";
import { Announcements } from "@/features/announcements/banner";
import { sessionQuery, useLogout } from "@/lib/api/queries";
import { applyTheme, currentTheme, type Theme } from "@/lib/theme";

/** Everything behind sign-in. */
export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session) throw redirect({ to: "/login" });
    return { session };
  },
  component: AppLayout,
});

function AppLayout() {
  const { session } = Route.useRouteContext();
  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-30 border-b border-line bg-bg/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4">
          <Link to="/" aria-label="Мои конфиги">
            <Brand />
          </Link>
          <nav className="flex items-center gap-1">
            {session.user.role === "admin" && (
              <Button asChild variant="ghost" size="sm">
                <Link to="/admin" activeProps={{ className: "bg-surface-2" }}>
                  <Shield aria-hidden />
                  Админка
                </Link>
              </Button>
            )}
            <AccountMenu username={session.user.username} />
          </nav>
        </div>
      </header>
      <Announcements />
      <Outlet />
    </div>
  );
}

function AccountMenu({ username }: { username: string }) {
  const navigate = useNavigate();
  const logout = useLogout();
  const [theme, setTheme] = useState<Theme>(currentTheme);
  const next: Theme = theme === "dark" ? "light" : "dark";

  return (
    <DropdownMenu
      trigger={
        <Button variant="ghost" size="sm" aria-label="Меню аккаунта">
          <UserRound aria-hidden />
          <span className="max-w-32 truncate">{username}</span>
        </Button>
      }
    >
      <MenuItem onSelect={() => navigate({ to: "/settings" })}>
        <Settings aria-hidden />
        Настройки
      </MenuItem>
      <MenuItem
        onSelect={(event) => {
          event.preventDefault();
          applyTheme(next);
          setTheme(next);
        }}
      >
        {next === "light" ? <Sun aria-hidden /> : <Moon aria-hidden />}
        {next === "light" ? "Светлая тема" : "Тёмная тема"}
      </MenuItem>
      <MenuSeparator />
      <MenuItem
        onSelect={() => logout.mutate(undefined, { onSettled: () => navigate({ to: "/login" }) })}
      >
        <LogOut aria-hidden />
        Выйти
      </MenuItem>
    </DropdownMenu>
  );
}
